import { supabase } from './supabaseClient';
import { config } from './config';

export interface EmailTemplate {
  subject: string;
  html: string;
  text: string;
}

// Base styling per email templates
const emailBaseStyles = `
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 0; background-color: #f8f9fa; }
  .container { max-width: 600px; margin: 0 auto; background-color: white; }
  .header { background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%); padding: 30px; text-align: center; }
  .header h1 { color: white; margin: 0; font-size: 28px; }
  .header p { color: white; margin: 10px 0 0 0; }
  .content { padding: 30px; }
  .footer { background-color: #f8f9fa; padding: 20px; text-align: center; font-size: 14px; color: #6b7280; }
  .button { display: inline-block; background: #0d9488; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; margin: 20px 0; }
  .highlight { background-color: #ecfdf5; padding: 15px; border-radius: 8px; margin: 15px 0; border-left: 4px solid #0d9488; }
  .booking-details { background-color: #f0fdf4; border: 2px solid #10b981; border-radius: 12px; padding: 20px; margin: 20px 0; }
  .detail-row { display: flex; justify-content: space-between; margin: 10px 0; padding: 5px 0; border-bottom: 1px solid #e5e7eb; }
  .detail-label { font-weight: bold; color: #374151; }
  .detail-value { color: #059669; font-weight: 500; }
  .alert { padding: 15px; border-radius: 8px; margin: 15px 0; }
  .alert-success { background-color: #ecfdf5; border-left: 4px solid #10b981; color: #065f46; }
  .alert-warning { background-color: #fffbeb; border-left: 4px solid #f59e0b; color: #92400e; }
  .alert-error { background-color: #fef2f2; border-left: 4px solid #ef4444; color: #991b1b; }
  .text-center { text-align: center; }
  .mb-20 { margin-bottom: 20px; }
`;

// Tipi per l'interfaccia email
export interface EmailData {
  to: string;
  subject: string;
  html?: string;
  text?: string;
  attachments?: Array<{
    filename: string;
    content: string;
    encoding?: string;
    contentType?: string;
  }>;
}

// Tipi per email di registrazione
export interface RegistrationEmailData {
  email: string;
  firstName?: string;
  lastName?: string;
  verificationToken?: string;
  language?: 'it' | 'en';
}

// Tipi per email di verifica
export interface VerificationEmailData {
  email: string;
  verificationCode: string;
  expiresAt: string;
  language?: 'it' | 'en';
}

// Tipi per la risposta del servizio email
export interface EmailResponse {
  success: boolean;
  messageId?: string;
  error?: string;
}

// URL del server email (ora usa la configurazione centrale)
const EMAIL_SERVER_URL = config.app.apiUrl;

// Frontend email service - chiamate al server email dedicato
export const sendEmail = async (
  to: string,
  template: EmailTemplate,
  templateData?: any
): Promise<{ success: boolean; messageId?: string; error?: string }> => {
  try {
    console.log(`📧 Invio email tramite server dedicato a: ${to}`);
    
    const response = await fetch(`${EMAIL_SERVER_URL}/api/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to,
        subject: template.subject,
        html: template.html,
        text: template.text,
        templateData
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Errore nell\'invio dell\'email');
    }

    console.log('✅ Email inviata con successo:', result.messageId);
    return { 
      success: true, 
      messageId: result.messageId 
    };
  } catch (error) {
    console.error('❌ Errore invio email:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore sconosciuto' 
    };
  }
};

// Funzione specializzata per email di registrazione
export const sendRegistrationEmail = async (
  email: string, 
  name: string, 
  verificationLink: string
): Promise<{ success: boolean; messageId?: string; error?: string }> => {
  try {
    console.log(`📧 Invio email registrazione a: ${email}`);
    
    const response = await fetch(`${EMAIL_SERVER_URL}/api/send-registration-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        name,
        verificationLink
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Errore nell\'invio dell\'email di registrazione');
    }

    console.log('✅ Email registrazione inviata:', result.messageId);

    // Mostra notifica di successo all'utente
    if (typeof window !== 'undefined') {
      const notification = document.createElement('div');
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        color: white;
        padding: 16px 20px;
        border-radius: 12px;
        box-shadow: 0 8px 25px rgba(16, 185, 129, 0.3);
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, sans-serif;
        font-size: 14px;
        max-width: 350px;
        animation: slideIn 0.5s ease-out;
      `;
      
      notification.innerHTML = `
        <div style="display: flex; align-items: center; gap: 12px;">
          <div style="font-size: 20px;">📧</div>
          <div>
            <div style="font-weight: bold; margin-bottom: 4px;">Email di conferma inviata!</div>
            <div style="opacity: 0.9; font-size: 13px;">Controlla la tua casella di posta per confermare l'account</div>
          </div>
        </div>
      `;
      
      // Aggiungi animazione CSS
      const style = document.createElement('style');
      style.textContent = `
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `;
      document.head.appendChild(style);
      
      document.body.appendChild(notification);
      
      // Rimuovi dopo 6 secondi con animazione
      setTimeout(() => {
        if (document.body.contains(notification)) {
          notification.style.transform = 'translateX(100%)';
          notification.style.opacity = '0';
          notification.style.transition = 'all 0.3s ease-out';
          setTimeout(() => {
            if (document.body.contains(notification)) {
              document.body.removeChild(notification);
            }
          }, 300);
        }
      }, 6000);
    }

    return { 
      success: true, 
      messageId: result.messageId 
    };
  } catch (error) {
    console.error('❌ Errore invio email registrazione:', error);
    
    // Mostra notifica di errore
    if (typeof window !== 'undefined') {
      const notification = document.createElement('div');
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        color: white;
        padding: 16px 20px;
        border-radius: 12px;
        box-shadow: 0 8px 25px rgba(239, 68, 68, 0.3);
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, sans-serif;
        font-size: 14px;
        max-width: 350px;
      `;
      
      notification.innerHTML = `
        <div style="display: flex; align-items: center; gap: 12px;">
          <div style="font-size: 20px;">❌</div>
          <div>
            <div style="font-weight: bold; margin-bottom: 4px;">Errore invio email</div>
            <div style="opacity: 0.9; font-size: 13px;">Si è verificato un errore. Riprova più tardi.</div>
          </div>
        </div>
      `;
      
      document.body.appendChild(notification);
      
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 5000);
    }

    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore sconosciuto' 
    };
  }
};

// Test connessione server email
export const testEmailConnection = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${EMAIL_SERVER_URL}/api/test-email-connection`);
    const result = await response.json();
    console.log('🔍 Test connessione email:', result);
    return result.success;
  } catch (error) {
    console.error('❌ Test connessione email fallito:', error);
    return false;
  }
};

// Template per email di registrazione (mantenuto per compatibilità)
export const getRegistrationEmailTemplate = (name: string, verificationLink: string): EmailTemplate => ({
  subject: '🏡 Conferma il tuo account J-O-H',
  html: `Template HTML sarà generato dal server...`,
  text: `Template testo sarà generato dal server...`
});

// Template per conferma prenotazione
export const getBookingConfirmationTemplate = (bookingData: {
  guestName: string;
  accommodationTitle: string;
  checkIn: string;
  checkOut: string;
  totalPrice: string;
  bookingId: string;
  address?: string;
  amenities?: string[];
}): EmailTemplate => ({
  subject: `🎉 Prenotazione confermata - ${bookingData.accommodationTitle}`,
  html: `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Prenotazione Confermata</title>
      <style>${emailBaseStyles}</style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>🎉 Prenotazione Confermata!</h1>
          <p>Il tuo soggiorno è stato prenotato con successo</p>
        </div>
        <div class="content">
          <h2>Ciao ${bookingData.guestName}!</h2>
          <p>Fantastico! La tua prenotazione è stata confermata. Non vediamo l'ora di ospitarti!</p>
          
          <div class="booking-details">
            <h3 style="margin-top: 0; color: #059669;">📋 Dettagli della prenotazione</h3>
            <div class="detail-row">
              <span class="detail-label">🏠 Alloggio:</span>
              <span class="detail-value">${bookingData.accommodationTitle}</span>
            </div>
            ${bookingData.address ? `
            <div class="detail-row">
              <span class="detail-label">📍 Indirizzo:</span>
              <span class="detail-value">${bookingData.address}</span>
            </div>
            ` : ''}
            <div class="detail-row">
              <span class="detail-label">📅 Check-in:</span>
              <span class="detail-value">${bookingData.checkIn}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">📅 Check-out:</span>
              <span class="detail-value">${bookingData.checkOut}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">💰 Totale pagato:</span>
              <span class="detail-value">${bookingData.totalPrice}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">🎫 ID Prenotazione:</span>
              <span class="detail-value">${bookingData.bookingId}</span>
            </div>
          </div>
          
          ${bookingData.amenities ? `
          <div class="highlight">
            <h3>🌟 Servizi inclusi:</h3>
            <ul>
              ${bookingData.amenities.map(amenity => `<li>${amenity}</li>`).join('')}
            </ul>
          </div>
          ` : ''}
          
          <h3>📞 Prossimi passi:</h3>
          <ul>
            <li>Riceverai le istruzioni per il check-in 24-48 ore prima del tuo arrivo</li>
            <li>Se hai domande, contattaci a <strong>info@dhevylgroup.com</strong></li>
            <li>Salva questo ID prenotazione: <strong>${bookingData.bookingId}</strong></li>
          </ul>
          
          <p>Grazie per aver scelto J-O-H! Il tuo soggiorno perfetto ti aspetta! 🏡✨</p>
        </div>
        <div class="footer">
          <p>© 2024 J-O-H - I tuoi alloggi da sogno</p>
          <p>Prenotazione ID: ${bookingData.bookingId}</p>
        </div>
      </div>
    </body>
    </html>
  `,
  text: `
    Prenotazione Confermata!
    
    Ciao ${bookingData.guestName}!
    
    La tua prenotazione è stata confermata con successo.
    
    Dettagli:
    - Alloggio: ${bookingData.accommodationTitle}
    - Check-in: ${bookingData.checkIn}
    - Check-out: ${bookingData.checkOut}
    - Totale: ${bookingData.totalPrice}
    - ID Prenotazione: ${bookingData.bookingId}
    
    Riceverai le istruzioni per il check-in 24-48 ore prima del tuo arrivo.
    
    Grazie per aver scelto J-O-H!
    
    © 2024 J-O-H - I tuoi alloggi da sogno
  `
});

// Template per promemoria check-in
export const getCheckInReminderTemplate = (bookingData: {
  guestName: string;
  accommodationTitle: string;
  checkInDate: string;
  checkInTime: string;
  address: string;
  accessInstructions: string;
  hostContact: string;
  bookingId: string;
}): EmailTemplate => ({
  subject: `🗝️ Promemoria Check-in - ${bookingData.accommodationTitle}`,
  html: `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Promemoria Check-in</title>
      <style>${emailBaseStyles}</style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>🗝️ Promemoria Check-in</h1>
          <p>Il tuo soggiorno inizia domani!</p>
        </div>
        <div class="content">
          <h2>Ciao ${bookingData.guestName}!</h2>
          <p>Il tuo check-in è programmato per domani. Ecco tutte le informazioni che ti servono:</p>
          
          <div class="booking-details">
            <h3 style="margin-top: 0; color: #059669;">🏠 Dettagli del check-in</h3>
            <div class="detail-row">
              <span class="detail-label">🏡 Alloggio:</span>
              <span class="detail-value">${bookingData.accommodationTitle}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">📍 Indirizzo:</span>
              <span class="detail-value">${bookingData.address}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">📅 Data check-in:</span>
              <span class="detail-value">${bookingData.checkInDate}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">⏰ Orario check-in:</span>
              <span class="detail-value">${bookingData.checkInTime}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">📞 Contatto host:</span>
              <span class="detail-value">${bookingData.hostContact}</span>
            </div>
          </div>
          
          <div class="alert alert-success">
            <h3>🔑 Istruzioni per l'accesso:</h3>
            <p>${bookingData.accessInstructions}</p>
          </div>
          
          <div class="highlight">
            <h3>📝 Cosa portare:</h3>
            <ul>
              <li>Documento di identità valido</li>
              <li>Conferma della prenotazione (ID: ${bookingData.bookingId})</li>
              <li>Numero di telefono di contatto aggiornato</li>
            </ul>
          </div>
          
          <p>Se hai domande o imprevisti, contatta immediatamente il nostro team!</p>
          
          <p>Buon viaggio! 🧳✨</p>
        </div>
        <div class="footer">
          <p>© 2024 J-O-H - I tuoi alloggi da sogno</p>
          <p>Prenotazione ID: ${bookingData.bookingId}</p>
        </div>
      </div>
    </body>
    </html>
  `,
  text: `
    Promemoria Check-in
    
    Ciao ${bookingData.guestName}!
    
    Il tuo check-in è programmato per domani:
    
    Alloggio: ${bookingData.accommodationTitle}
    Indirizzo: ${bookingData.address}
    Data: ${bookingData.checkInDate}
    Orario: ${bookingData.checkInTime}
    Contatto: ${bookingData.hostContact}
    
    Istruzioni accesso:
    ${bookingData.accessInstructions}
    
    Cosa portare:
    - Documento di identità valido
    - Conferma prenotazione (ID: ${bookingData.bookingId})
    - Numero di telefono aggiornato
    
    Buon viaggio!
    
    © 2024 J-O-H - I tuoi alloggi da sogno
  `
});

// Template per cancellazione prenotazione
export const getBookingCancellationTemplate = (cancellationData: {
  guestName: string;
  accommodationTitle: string;
  bookingId: string;
  cancellationDate: string;
  refundAmount?: string;
  refundTimeframe?: string;
}): EmailTemplate => ({
  subject: `❌ Cancellazione confermata - ${cancellationData.accommodationTitle}`,
  html: `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Cancellazione Confermata</title>
      <style>${emailBaseStyles}</style>
    </head>
    <body>
      <div class="container">
        <div class="header" style="background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);">
          <h1>❌ Cancellazione Confermata</h1>
          <p>La tua prenotazione è stata cancellata</p>
        </div>
        <div class="content">
          <h2>Ciao ${cancellationData.guestName},</h2>
          <p>La tua prenotazione è stata cancellata con successo come richiesto.</p>
          
          <div class="booking-details">
            <h3 style="margin-top: 0; color: #dc2626;">📋 Dettagli cancellazione</h3>
            <div class="detail-row">
              <span class="detail-label">🏠 Alloggio:</span>
              <span class="detail-value">${cancellationData.accommodationTitle}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">🎫 ID Prenotazione:</span>
              <span class="detail-value">${cancellationData.bookingId}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">📅 Data cancellazione:</span>
              <span class="detail-value">${cancellationData.cancellationDate}</span>
            </div>
            ${cancellationData.refundAmount ? `
            <div class="detail-row">
              <span class="detail-label">💰 Rimborso:</span>
              <span class="detail-value">${cancellationData.refundAmount}</span>
            </div>
            ` : ''}
          </div>
          
          ${cancellationData.refundAmount ? `
          <div class="alert alert-success">
            <h3>💳 Informazioni rimborso:</h3>
            <p>Il rimborso di <strong>${cancellationData.refundAmount}</strong> verrà elaborato entro ${cancellationData.refundTimeframe || '5-7 giorni lavorativi'} sul metodo di pagamento originale.</p>
          </div>
          ` : ''}
          
          <p>Ci dispiace che tu non possa più soggiornare con noi. Speriamo di ospitarti in una prossima occasione!</p>
          
          <div class="text-center">
            <a href="/accommodations" class="button">Scopri Altri Alloggi</a>
          </div>
        </div>
        <div class="footer">
          <p>© 2024 J-O-H - I tuoi alloggi da sogno</p>
          <p>Cancellazione ID: ${cancellationData.bookingId}</p>
        </div>
      </div>
    </body>
    </html>
  `,
  text: `
    Cancellazione Confermata
    
    Ciao ${cancellationData.guestName},
    
    La tua prenotazione è stata cancellata con successo.
    
    Dettagli:
    - Alloggio: ${cancellationData.accommodationTitle}
    - ID Prenotazione: ${cancellationData.bookingId}
    - Data cancellazione: ${cancellationData.cancellationDate}
    ${cancellationData.refundAmount ? `- Rimborso: ${cancellationData.refundAmount}` : ''}
    
    ${cancellationData.refundAmount ? `Il rimborso verrà elaborato entro ${cancellationData.refundTimeframe || '5-7 giorni lavorativi'}.` : ''}
    
    Speriamo di ospitarti in una prossima occasione!
    
    © 2024 J-O-H - I tuoi alloggi da sogno
  `
});

// Template per offerte speciali
export const getSpecialOfferTemplate = (offerData: {
  guestName: string;
  offerTitle: string;
  discountPercentage: number;
  validUntil: string;
  accommodationTitle?: string;
  description: string;
  bookingLink: string;
  offerCode?: string;
}): EmailTemplate => ({
  subject: `🎁 Offerta Speciale: ${offerData.discountPercentage}% di sconto - ${offerData.offerTitle}`,
  html: `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Offerta Speciale</title>
      <style>${emailBaseStyles}</style>
    </head>
    <body>
      <div class="container">
        <div class="header" style="background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);">
          <h1>🎁 Offerta Speciale!</h1>
          <p>Solo per te: ${offerData.discountPercentage}% di sconto</p>
        </div>
        <div class="content">
          <h2>Ciao ${offerData.guestName}!</h2>
          <p>Abbiamo un'offerta speciale che non puoi perdere!</p>
          
          <div class="highlight">
            <h3 style="color: #f59e0b;">🌟 ${offerData.offerTitle}</h3>
            <p style="font-size: 18px; font-weight: bold; color: #f59e0b;">
              ${offerData.discountPercentage}% DI SCONTO
            </p>
            <p>${offerData.description}</p>
            ${offerData.accommodationTitle ? `<p><strong>Alloggio in offerta:</strong> ${offerData.accommodationTitle}</p>` : ''}
          </div>
          
          ${offerData.offerCode ? `
          <div class="alert alert-warning">
            <h3>🏷️ Codice sconto:</h3>
            <p style="font-size: 20px; font-weight: bold; text-align: center; padding: 10px; background: #fff; border-radius: 5px;">
              ${offerData.offerCode}
            </p>
            <p><small>Copia questo codice e utilizzalo durante la prenotazione</small></p>
          </div>
          ` : ''}
          
          <div class="alert alert-error">
            <h3>⏰ Offerta limitata!</h3>
            <p>Questa offerta è valida fino al <strong>${offerData.validUntil}</strong>. Non perdere questa opportunità!</p>
          </div>
          
          <div class="text-center">
            <a href="${offerData.bookingLink}" class="button" style="background: #f59e0b; font-size: 18px; padding: 20px 40px;">
              Prenota Ora 🚀
            </a>
          </div>
          
          <p>Affrettati, le offerte speciali vanno a ruba! 💨</p>
        </div>
        <div class="footer">
          <p>© 2024 J-O-H - I tuoi alloggi da sogno</p>
          <p>Valido fino al ${offerData.validUntil}</p>
        </div>
      </div>
    </body>
    </html>
  `,
  text: `
    Offerta Speciale!
    
    Ciao ${offerData.guestName}!
    
    ${offerData.offerTitle}
    ${offerData.discountPercentage}% DI SCONTO
    
    ${offerData.description}
    ${offerData.accommodationTitle ? `Alloggio: ${offerData.accommodationTitle}` : ''}
    
    ${offerData.offerCode ? `Codice sconto: ${offerData.offerCode}` : ''}
    
    Valido fino al ${offerData.validUntil}
    
    Prenota ora: ${offerData.bookingLink}
    
    © 2024 J-O-H - I tuoi alloggi da sogno
  `
});

// Template per richiesta recensione
export const getReviewRequestTemplate = (reviewData: {
  guestName: string;
  accommodationTitle: string;
  stayDates: string;
  reviewLink: string;
  bookingId: string;
}): EmailTemplate => ({
  subject: `⭐ Come è stato il tuo soggiorno? - ${reviewData.accommodationTitle}`,
  html: `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Richiesta Recensione</title>
      <style>${emailBaseStyles}</style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>⭐ Come è stato il soggiorno?</h1>
          <p>La tua opinione è importante per noi</p>
        </div>
        <div class="content">
          <h2>Ciao ${reviewData.guestName}!</h2>
          <p>Speriamo che tu abbia trascorso un soggiorno meraviglioso presso <strong>${reviewData.accommodationTitle}</strong>!</p>
          
          <div class="booking-details">
            <h3 style="margin-top: 0; color: #059669;">🏠 Il tuo soggiorno</h3>
            <div class="detail-row">
              <span class="detail-label">🏡 Alloggio:</span>
              <span class="detail-value">${reviewData.accommodationTitle}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">📅 Date soggiorno:</span>
              <span class="detail-value">${reviewData.stayDates}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">🎫 ID Prenotazione:</span>
              <span class="detail-value">${reviewData.bookingId}</span>
            </div>
          </div>
          
          <p>Ci piacerebbe conoscere la tua esperienza! La tua recensione ci aiuta a migliorare e aiuta altri viaggiatori a scegliere.</p>
          
          <div class="highlight">
            <h3>🌟 Lascia una recensione e ottieni:</h3>
            <ul>
              <li>🎁 <strong>5% di sconto</strong> sulla prossima prenotazione</li>
              <li>⭐ <strong>Status privilegiato</strong> per offerte esclusive</li>
              <li>💝 La gratitudine di futuri ospiti</li>
            </ul>
          </div>
          
          <div class="text-center">
            <a href="${reviewData.reviewLink}" class="button" style="font-size: 18px; padding: 20px 40px;">
              Scrivi Recensione ⭐
            </a>
          </div>
          
          <p>Bastano solo 2 minuti per condividere la tua esperienza!</p>
          
          <p>Grazie per aver scelto J-O-H! 🏡💙</p>
        </div>
        <div class="footer">
           <p>Prenotazione ID: ${reviewData.bookingId}</p>
         </div>
       </div>
     </body>
     </html>
   `,
   text: `
     Come è stato il soggiorno?
     
     Ciao ${reviewData.guestName}!
     
     Speriamo che tu abbia trascorso un soggiorno meraviglioso!
     
     Il tuo soggiorno:
     - Alloggio: ${reviewData.accommodationTitle}
     - Date: ${reviewData.stayDates}
     - ID Prenotazione: ${reviewData.bookingId}
     
     Lascia una recensione e ottieni:
     - 5% di sconto sulla prossima prenotazione
     - Status privilegiato per offerte esclusive
     - La gratitudine di futuri ospiti
     
     Scrivi la tua recensione: ${reviewData.reviewLink}
     
     Grazie per aver scelto J-O-H!
     
     © 2024 J-O-H - I tuoi alloggi da sogno
   `
 });

// Template per reset password
export const getPasswordResetTemplate = (resetData: {
  userName: string;
  resetLink: string;
  expirationTime: string;
}): EmailTemplate => ({
  subject: '🔒 Reset della password - J-O-H',
  html: `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Reset Password</title>
      <style>${emailBaseStyles}</style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>🔒 Reset Password</h1>
          <p>Recupera l'accesso al tuo account</p>
        </div>
        <div class="content">
          <h2>Ciao ${resetData.userName}!</h2>
          <p>Hai richiesto di reimpostare la password del tuo account J-O-H.</p>
          
          <div class="alert alert-warning">
            <h3>🔐 Sicurezza del tuo account</h3>
            <p>Se non hai richiesto tu questo reset, ignora questa email. Il tuo account rimane sicuro.</p>
          </div>
          
          <p>Per reimpostare la password, clicca sul pulsante qui sotto:</p>
          
          <div class="text-center">
            <a href="${resetData.resetLink}" class="button">Reimposta Password 🔑</a>
          </div>
          
          <p><small>Se il pulsante non funziona, copia e incolla questo link nel tuo browser:</small></p>
          <p style="word-break: break-all; font-size: 12px; color: #6b7280;">${resetData.resetLink}</p>
          
          <div class="alert alert-error">
            <h3>⏰ Link temporaneo</h3>
            <p>Questo link scadrà il <strong>${resetData.expirationTime}</strong>. Se scade, dovrai richiedere un nuovo reset.</p>
          </div>
          
          <p>Per la sicurezza del tuo account, ti consigliamo di scegliere una password forte e unica.</p>
        </div>
        <div class="footer">
          <p>© 2024 J-O-H - I tuoi alloggi da sogno</p>
          <p>Se non hai richiesto questo reset, ignora questa email.</p>
        </div>
      </div>
    </body>
    </html>
  `,
  text: `
    Reset Password - J-O-H
    
    Ciao ${resetData.userName}!
    
    Hai richiesto di reimpostare la password del tuo account J-O-H.
    
    Se non hai richiesto tu questo reset, ignora questa email.
    
    Per reimpostare la password, visita:
    ${resetData.resetLink}
    
    Questo link scadrà il ${resetData.expirationTime}.
    
    © 2024 J-O-H - I tuoi alloggi da sogno
  `
});

// Template per newsletter mensile
export const getNewsletterTemplate = (newsletterData: {
  subscriberName: string;
  monthYear: string;
  featuredProperty: {
    title: string;
    image: string;
    description: string;
    price: string;
    link: string;
  };
  newProperties: Array<{
    title: string;
    location: string;
    price: string;
    link: string;
  }>;
  specialOffers: Array<{
    title: string;
    discount: string;
    description: string;
    link: string;
  }>;
}): EmailTemplate => ({
  subject: `🏡 Newsletter J-O-H ${newsletterData.monthYear} - Nuovi alloggi e offerte`,
  html: `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Newsletter J-O-H</title>
      <style>${emailBaseStyles}</style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>🏡 Newsletter J-O-H</h1>
          <p>${newsletterData.monthYear} - Novità e offerte</p>
        </div>
        <div class="content">
          <h2>Ciao ${newsletterData.subscriberName}!</h2>
          <p>Ecco le novità del mese per i nostri membri più fedeli!</p>
          
          <div class="highlight">
            <h3>🌟 Proprietà in evidenza: ${newsletterData.featuredProperty.title}</h3>
            <p>${newsletterData.featuredProperty.description}</p>
            <p><strong>A partire da ${newsletterData.featuredProperty.price}/notte</strong></p>
            <div class="text-center">
              <a href="${newsletterData.featuredProperty.link}" class="button">Scopri di Più</a>
            </div>
          </div>
          
          <h3>🏠 Nuove Proprietà</h3>
          ${newsletterData.newProperties.map(property => `
          <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 15px; margin: 10px 0;">
            <h4>${property.title}</h4>
            <p>📍 ${property.location} | 💰 ${property.price}/notte</p>
            <a href="${property.link}" style="color: #0d9488; text-decoration: none;">Vedi dettagli →</a>
          </div>
          `).join('')}
          
          <h3>🎁 Offerte Speciali</h3>
          ${newsletterData.specialOffers.map(offer => `
          <div class="alert alert-success">
            <h4>${offer.title} - ${offer.discount}</h4>
            <p>${offer.description}</p>
            <a href="${offer.link}" class="button" style="display: inline-block; margin-top: 10px;">Approfitta Ora</a>
          </div>
          `).join('')}
          
          <p>Grazie per essere parte della famiglia J-O-H! 💙</p>
        </div>
        <div class="footer">
          <p>© 2024 J-O-H - I tuoi alloggi da sogno</p>
          <p><a href="#" style="color: #6b7280;">Disiscriviti</a> | <a href="#" style="color: #6b7280;">Aggiorna preferenze</a></p>
        </div>
      </div>
    </body>
    </html>
  `,
  text: `
    Newsletter J-O-H ${newsletterData.monthYear}
    
    Ciao ${newsletterData.subscriberName}!
    
    PROPRIETÀ IN EVIDENZA: ${newsletterData.featuredProperty.title}
    ${newsletterData.featuredProperty.description}
    A partire da ${newsletterData.featuredProperty.price}/notte
    Link: ${newsletterData.featuredProperty.link}
    
    NUOVE PROPRIETÀ:
    ${newsletterData.newProperties.map(property => 
      `- ${property.title} (${property.location}) - ${property.price}/notte: ${property.link}`
    ).join('\n')}
    
    OFFERTE SPECIALI:
    ${newsletterData.specialOffers.map(offer =>
      `- ${offer.title} - ${offer.discount}: ${offer.description} | ${offer.link}`
    ).join('\n')}
    
    Grazie per essere parte della famiglia J-O-H!
    
    © 2024 J-O-H - I tuoi alloggi da sogno
  `
});

// Template per verifica telefono (SMS)
export const getPhoneVerificationTemplate = (code: string): string => 
  `J-O-H: Il tuo codice di verifica è ${code}. Non condividerlo con nessuno. Valido per 10 minuti.`; 